import { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  Employee360Response,
  HrApprovalRequest,
  HrWorkflowConfig,
  HrManagerTeamSummary,
  HrLifecycleEvent,
  HrOrgStructure,
  HrAttendance,
  HrDashboardSummary,
  HrExpenseClaim,
  HrTravelRequest,
  HrJobPosting,
  HrLeaveRequest,
  HrPayrollRun,
  HrPayrollTransaction,
  HrRecruitmentPipeline,
  HrRecruitmentPipelineStage,
  HrRecruitmentDashboard,
  HrManpowerRequest,
  HrJobRequisition,
  HrOnboardingTask,
  HrPreboardingItem,
  HrWorkforceAnalytics,
  HrPayrollFormula,
  HrPayrollHandoffResponse,
  HrAttendanceDevice,
  HrFeedback360Summary,
  HrTrainingCourse,
  HrAppraisalCycle,
  HrEmployeeReview,
  HrLoanRequest,
  HrAssetRequest,
  HrShift,
  HrDocumentType,
  HrEmployeeDocumentRecord,
  HrEmploymentType,
  HrLeavePolicy,
  HrLeaveType,
  HrOvertimeType,
  HrShiftType,
  HrExpenseType,
  HrAllowanceAssignment,
  HrAllowanceAssignmentHistory,
  HrAllowanceSummary,
  HrAllowanceType,
  HrAssetType,
  HrEmploymentHistory,
  HrExpectedHours,
  HrLeaveDaysPreview,
  HrCompanyHoliday,
  HrHolidayType,
  HrResolvedCalendar,
  HrMonthlyAttendanceSummary,
  HrEmployeeWorkforceDashboard,
  HrManagerWorkforceDashboard,
  HrHrWorkforceDashboard,
  HrWorkCalendarOverride,
  HrWeeklyOvertimeSummary,
  HrWorkforceSettings,
  HrTimesheetDay,
  HrTimesheetSummary,
  HrCompRestOverview,
  HrEssTimesheet,
  HrAbsenceCategory,
  HrRosterCopyRequest,
  HrRosterCopyPreview,
  HrRosterCopyResult,
  HrShiftSwapRequest,
} from '@/types/hr';
import type { EntityOptionResponse } from '@/types/entityOption';
import type { EmployeeResponse } from '@/types/employee';
import api from './client';

export const hrApi = {
  dashboardSummary: (period: import('@/types/dashboardPresentation').DashboardPeriod = 'MONTH') =>
    unwrap(api.get<ApiResponse<HrDashboardSummary>>('/v1/hr/dashboard/summary', { params: { period } })),

  employee360: (employeeId: number) =>
    unwrap(api.get<ApiResponse<Employee360Response>>(`/v1/hr/employees/${employeeId}/360`)),

  seedSetup: () => unwrap(api.post<ApiResponse<Record<string, number>>>('/v1/hr/setup/seed')),
  getSetup: () => unwrap(api.get<ApiResponse<Record<string, unknown>>>('/v1/hr/setup')),

  upsertEmployeeProfile: (body: {
    employeeId: number;
    departmentId?: number;
    designationId?: number;
    gradeId?: number;
    managerId?: number;
    businessUnitId?: number;
    employmentType?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/employee/profile', body)),

  markAttendance: (body: {
    employeeId?: number;
    shiftId?: number;
    checkIn?: string;
    checkOut?: string;
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    overtimeType?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>('/v1/hr/attendance', body)),

  listVisibleEmployees: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<EmployeeResponse>>>('/v1/hr/employees', { params })),

  listVisibleEmployeeOptions: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<EntityOptionResponse>>>('/v1/hr/employees/options', { params })),

  getVisibleEmployee: (employeeId: number) =>
    unwrap(api.get<ApiResponse<EmployeeResponse>>(`/v1/hr/employees/${employeeId}`)),

  checkOutAttendance: (id: number, body?: {
    checkOut?: string;
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    overtimeType?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}/check-out`, body ?? {})),

  getAttendance: (id: number) =>
    unwrap(api.get<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}`)),

  updateAttendance: (
    id: number,
    body: {
      employeeId: number;
      shiftId?: number;
      checkIn?: string;
      checkOut?: string;
      overtimeType?: string;
    },
  ) => unwrap(api.put<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}`, body)),

  createLeave: (body: {
    employeeId: number;
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    startTime?: string;
    endTime?: string;
    shiftId?: number;
    affectedShiftIds?: number[];
    attachmentUrl?: string;
  }) => unwrap(api.post<ApiResponse<HrLeaveRequest>>('/v1/hr/leaves', body)),

  approveLeave: (id: number, approverId: number) =>
    unwrap(api.post<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}/approve`, null, { params: { approverId } })),

  rejectLeave: (id: number, approverId: number) =>
    unwrap(api.post<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}/reject`, null, { params: { approverId } })),

  updateLeave: (id: number, body: {
    employeeId: number;
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    attachmentUrl?: string;
  }) => unwrap(api.put<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}`, body)),

  getLeave: (id: number) => unwrap(api.get<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}`)),

  cancelLeave: (id: number) =>
    unwrap(api.post<ApiResponse<HrLeaveRequest>>(`/v1/hr/leaves/${id}/cancel`)),

  cancelAttendance: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrAttendance>>(`/v1/hr/attendance/${id}/cancel`)),

  listAttendance: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrAttendance>>>('/v1/hr/attendance', { params })),

  listLeaves: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrLeaveRequest>>>('/v1/hr/leaves', { params })),

  getWorkforceSettings: () =>
    unwrap(api.get<ApiResponse<HrWorkforceSettings>>('/v1/hr/platform/workforce/settings')),

  saveWorkforceSettings: (body: Partial<HrWorkforceSettings>) =>
    unwrap(api.put<ApiResponse<HrWorkforceSettings>>('/v1/hr/platform/workforce/settings', body)),

  createPayrollRun: (body: {
    runCode: string;
    periodStart: string;
    periodEnd: string;
    currencyCode?: string;
  }) => unwrap(api.post<ApiResponse<HrPayrollRun>>('/v1/hr/payroll/runs', body)),

  listPayrollRuns: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrPayrollRun>>>('/v1/hr/payroll/runs', { params })),

  approvePayrollRun: (id: number) =>
    unwrap(api.post<ApiResponse<HrPayrollRun>>(`/v1/hr/payroll/runs/${id}/approve`)),

  processPayrollRun: (id: number, totalAmount: number) =>
    unwrap(
      api.post<ApiResponse<HrPayrollRun>>(`/v1/hr/payroll/runs/${id}/process`, null, {
        params: { totalAmount },
      }),
    ),

  listExpenses: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<HrExpenseClaim>>>('/v1/hr/expenses', { params })),

  listTrainingCourses: () =>
    unwrap(api.get<ApiResponse<HrTrainingCourse[]>>('/v1/hr/training/courses')),

  createTrainingCourse: (body: { title: string; category?: string; provider?: string }) =>
    unwrap(api.post<ApiResponse<HrTrainingCourse>>('/v1/hr/training/courses', body)),

  createExpense: (body: {
    employeeId: number;
    claimDate: string;
    totalAmount: number;
    currencyCode?: string;
    expenseTypeCode: string;
    description: string;
    attachmentUrl?: string;
  }) => unwrap(api.post<ApiResponse<HrExpenseClaim>>('/v1/hr/expenses', body)),

  updateExpense: (
    id: number,
    body: {
      employeeId: number;
      claimDate: string;
      totalAmount: number;
      currencyCode?: string;
      expenseTypeCode: string;
      description: string;
      attachmentUrl?: string;
    },
  ) => unwrap(api.put<ApiResponse<HrExpenseClaim>>(`/v1/hr/expenses/${id}`, body)),

  approveExpense: (id: number, comments?: string) =>
    unwrap(
      api.post<ApiResponse<HrExpenseClaim>>(`/v1/hr/expenses/${id}/approve`, null, {
        params: comments ? { comments } : undefined,
      }),
    ),

  listApprovalRequests: (status?: string, mineOnly?: boolean) =>
    unwrap(
      api.get<ApiResponse<HrApprovalRequest[]>>('/v1/hr/approvals', {
        params: { ...(status ? { status } : {}), ...(mineOnly ? { mineOnly: true } : {}) },
      }),
    ),

  actApproval: (id: number, action: 'APPROVE' | 'REJECT', comments?: string) =>
    unwrap(
      api.post<ApiResponse<HrApprovalRequest>>(`/v1/hr/approvals/${id}/act`, null, {
        params: { action, ...(comments ? { comments } : {}) },
      }),
    ),

  listJobs: () => unwrap(api.get<ApiResponse<HrJobPosting[]>>('/v1/hr/recruitment/jobs')),

  createJobPosting: (body: {
    title: string;
    departmentId?: number;
    description?: string;
    location?: string;
    employmentType?: string;
    positionsCount?: number;
    budgetAmount?: number;
    postingChannels?: string;
    statusCode?: string;
  }) => unwrap(api.post<ApiResponse<HrJobPosting>>('/v1/hr/recruitment/jobs', body)),

  addCandidate: (body: {
    jobPostingId?: number;
    fullName: string;
    email?: string;
    phone?: string;
    source?: string;
    skills?: string;
    expectedSalary?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/recruitment/candidates', body)),

  moveCandidateStage: (id: number, stage: string) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${id}/stage`, null, { params: { stage } })),

  recruitmentPipeline: (search?: string) =>
    unwrap(
      api.get<ApiResponse<HrRecruitmentPipeline>>('/v1/hr/recruitment/pipeline/v2', {
        params: search ? { search } : undefined,
      }),
    ),

  runLeaveAccrual: () => unwrap(api.post<ApiResponse<{ balancesUpdated: number }>>('/v1/hr/leave/accrual/run')),

  leaveBalances: (employeeId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`/v1/hr/leave/balances/${employeeId}`)),

  leaveBalanceSummary: (employeeId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrLeaveBalanceView[]>>(`/v1/hr/leave/balances/${employeeId}/summary`)),

  essLeaveBalanceSummary: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrLeaveBalanceView[]>>('/v1/hr/ess/leave/balances/summary')),

  adjustLeaveBalance: (body: {
    employeeId: number;
    leaveTypeId: number;
    daysDelta: number;
    reason?: string;
    year?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/leave/balances/adjust', body)),

  runLeaveYearEnd: (fromYear?: number) =>
    unwrap(
      api.post<ApiResponse<{ recordsProcessed: number }>>('/v1/hr/leave/year-end/run', null, {
        params: fromYear != null ? { fromYear } : undefined,
      }),
    ),

  leaveRequestHistory: (leaveRequestId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrLeaveHistoryView>>(`/v1/hr/leave/requests/${leaveRequestId}/history`)),

  calculatePayroll: (id: number, countryCode?: string) =>
    unwrap(
      api.post<ApiResponse<HrPayrollRun>>(`/v1/hr/payroll/runs/${id}/calculate`, null, {
        params: { countryCode },
      }),
    ),

  workforceAnalytics: () =>
    unwrap(api.get<ApiResponse<HrWorkforceAnalytics>>('/v1/hr/analytics/workforce')),

  essProfile: () => unwrap(api.get<ApiResponse<Employee360Response>>('/v1/hr/ess/me')),

  essUpdateBankDetails: (body: {
    bankName?: string;
    bankBranch?: string;
    bankAccountNumber?: string;
    bankIfscCode?: string;
  }) =>
    unwrap(
      api.put<ApiResponse<Employee360Response['bankDetails']>>('/v1/hr/ess/me/bank-details', body),
    ),

  updateEmployeeBankDetails: (
    employeeId: number,
    body: {
      bankName?: string;
      bankBranch?: string;
      bankAccountNumber?: string;
      bankIfscCode?: string;
    },
  ) =>
    unwrap(
      api.put<ApiResponse<Employee360Response['bankDetails']>>(
        `/v1/hr/employees/${employeeId}/bank-details`,
        body,
      ),
    ),
  essWorkspace: (start?: string, end?: string) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrEssWorkspace>>('/v1/hr/ess/workspace', {
        params: { start, end },
      }),
    ),

  essSubmittedApprovals: () =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalInstance[]>>('/v1/hr/ess/approvals/submitted'),
    ),

  essLeaveApproval: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalInstance | null>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/approval`,
      ),
    ),

  essLeaveCancelApproval: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalInstance | null>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/cancellation/approval`,
      ),
    ),

  essLeaveApprovalTimeline: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalActionRecord[]>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/approval/timeline`,
      ),
    ),

  essLeaveCancelApprovalTimeline: (leaveRequestId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/approvals').ApprovalActionRecord[]>>(
        `/v1/hr/ess/leave/requests/${leaveRequestId}/cancellation/approval/timeline`,
      ),
    ),

  essApplyLeave: (body: {
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    attachmentUrl?: string;
  }) => unwrap(api.post<ApiResponse<HrLeaveRequest>>('/v1/hr/ess/leave', body)),

  essPreviewLeaveDays: (
    selectedDates: string[],
    opts?: { durationMode?: string; halfDaySession?: string; leaveHours?: number },
  ) => {
    const sorted = [...selectedDates].sort();
    return unwrap(
      api.get<ApiResponse<import('@/types/hr').HrLeaveDaysPreview>>('/v1/hr/ess/leave/days-preview', {
        params: {
          selectedDates: sorted,
          startDate: sorted[0],
          endDate: sorted[sorted.length - 1],
          ...opts,
        },
      }),
    );
  },

  essGetLeave: (id: number) =>
    unwrap(api.get<ApiResponse<HrLeaveRequest>>(`/v1/hr/ess/leave/requests/${id}`)),

  essUpdateLeave: (id: number, body: {
    leaveTypeId: number;
    startDate?: string;
    endDate?: string;
    selectedDates?: string[];
    reason?: string;
    durationMode?: string;
    halfDaySession?: string;
    leaveHours?: number;
    attachmentUrl?: string;
  }) => unwrap(api.put<ApiResponse<HrLeaveRequest>>(`/v1/hr/ess/leave/requests/${id}`, body)),

  essCheckIn: (body: {
    checkIn?: string;
    checkOut?: string;
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    shiftId?: number;
    deviceFingerprint?: string;
    devicePlatform?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>('/v1/hr/ess/attendance/check-in', body)),

  essCheckOut: (body: {
    checkInMode?: string;
    checkInLatitude?: number;
    checkInLongitude?: number;
    checkOut?: string;
    deviceFingerprint?: string;
    devicePlatform?: string;
  }) => unwrap(api.post<ApiResponse<HrAttendance>>('/v1/hr/ess/attendance/check-out', body)),

  essMobileDeviceStatus: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrEssMobileDeviceStatus>>('/v1/hr/ess/mobile-device')),

  getEmployeeMobileDevice: (employeeId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrEmployeeMobileDevice | null>>(
        `/v1/hr/employees/${employeeId}/mobile-device`,
      ),
    ),

  bindEmployeeMobileDevice: (
    employeeId: number,
    body: { deviceFingerprint: string; deviceLabel?: string; platform?: string },
  ) =>
    unwrap(
      api.put<ApiResponse<import('@/types/hr').HrEmployeeMobileDevice>>(
        `/v1/hr/employees/${employeeId}/mobile-device`,
        body,
      ),
    ),

  revokeEmployeeMobileDevice: (employeeId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/employees/${employeeId}/mobile-device`)),

  listFormulas: (countryCode?: string) =>
    unwrap(api.get<ApiResponse<HrPayrollFormula[]>>('/v1/hr/payroll/formulas', { params: { countryCode } })),

  listDevices: () => unwrap(api.get<ApiResponse<HrAttendanceDevice[]>>('/v1/hr/devices')),

  registerDevice: (body: { deviceName: string; deviceCode: string; deviceType: string; location?: string }) =>
    unwrap(api.post<ApiResponse<HrAttendanceDevice>>('/v1/hr/devices/register', body)),

  launchFeedback360: (cycleId: number, revieweeEmployeeId: number) =>
    unwrap(
      api.post<ApiResponse<unknown>>(`/v1/hr/feedback/cycles/${cycleId}/launch`, null, {
        params: { revieweeEmployeeId },
      }),
    ),

  feedbackSummary: (cycleId: number, revieweeEmployeeId: number) =>
    unwrap(
      api.get<ApiResponse<HrFeedback360Summary>>('/v1/hr/feedback/summary', {
        params: { cycleId, revieweeEmployeeId },
      }),
    ),

  exportReport: async (
    reportType: string,
    format: 'CSV' | 'EXCEL',
    params?: { dateFrom?: string; dateTo?: string; status?: string; employeeId?: number },
  ) => {
    const res = await api.get(`/v1/hr/reports/export`, {
      params: {
        reportType,
        format,
        ...(params?.dateFrom ? { dateFrom: params.dateFrom } : {}),
        ...(params?.dateTo ? { dateTo: params.dateTo } : {}),
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.employeeId ? { employeeId: params.employeeId } : {}),
      },
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  downloadPayslipPdf: (payrollRunId: number, employeeId: number) =>
    api
      .get(`/v1/hr/reports/payslip/${payrollRunId}/${employeeId}`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  downloadEssPayslipPdf: (payrollRunId: number) =>
    api.get(`/v1/hr/ess/payslips/${payrollRunId}/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob),

  downloadStatutoryIndiaCsv: (payrollRunId: number) =>
    api
      .get(`/v1/hr/reports/statutory/india/${payrollRunId}`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  submitStatutoryFiling: (payrollRunId: number, portalCode: 'EPFO' | 'ESIC' | 'TRACES') =>
    unwrap(
      api.post<ApiResponse<{ id: number; statusCode: string; referenceNumber?: string }>>(
        `/v1/hr/reports/statutory/india/${payrollRunId}/file/${portalCode}`,
      ),
    ),

  listStatutoryFilings: (payrollRunId: number) =>
    unwrap(
      api.get<ApiResponse<Array<{ id: number; portalCode: string; statusCode: string; referenceNumber?: string }>>>(
        `/v1/hr/reports/statutory/india/${payrollRunId}/filings`,
      ),
    ),

  listAppraisalCycles: () =>
    unwrap(api.get<ApiResponse<HrAppraisalCycle[]>>('/v1/hr/appraisal/cycles')),

  createAppraisalCycle: (body: {
    cycleName: string;
    startDate: string;
    endDate: string;
    statusCode?: string;
  }) => unwrap(api.post<ApiResponse<HrAppraisalCycle>>('/v1/hr/appraisal/cycles', body)),

  listAppraisalReviews: (cycleId: number) =>
    unwrap(api.get<ApiResponse<HrEmployeeReview[]>>(`/v1/hr/appraisal/cycles/${cycleId}/reviews`)),

  submitAppraisalReview: (body: {
    appraisalCycleId: number;
    employeeId: number;
    reviewerId?: number;
    score?: number;
    comments?: string;
    statusCode?: string;
  }) => unwrap(api.post<ApiResponse<HrEmployeeReview>>('/v1/hr/appraisal/reviews', body)),

  approveAppraisalReview: (id: number) =>
    unwrap(api.post<ApiResponse<HrEmployeeReview>>(`/v1/hr/appraisal/reviews/${id}/approve`)),

  listLoans: () => unwrap(api.get<ApiResponse<HrLoanRequest[]>>('/v1/hr/loans')),

  createLoan: (body: {
    employeeId: number;
    loanType?: string;
    principalAmount: number;
    currencyCode?: string;
    tenureMonths?: number;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrLoanRequest>>('/v1/hr/loans', body)),

  submitLoan: (id: number) => unwrap(api.post<ApiResponse<HrLoanRequest>>(`/v1/hr/loans/${id}/submit`)),

  approveLoan: (id: number) => unwrap(api.post<ApiResponse<HrLoanRequest>>(`/v1/hr/loans/${id}/approve`)),

  listAssetRequests: () => unwrap(api.get<ApiResponse<HrAssetRequest[]>>('/v1/hr/asset-requests')),

  createAssetRequest: (body: {
    employeeId: number;
    assetType: string;
    quantity?: number;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrAssetRequest>>('/v1/hr/asset-requests', body)),

  submitAssetRequest: (id: number) =>
    unwrap(api.post<ApiResponse<HrAssetRequest>>(`/v1/hr/asset-requests/${id}/submit`)),

  approveAssetRequest: (id: number) =>
    unwrap(api.post<ApiResponse<HrAssetRequest>>(`/v1/hr/asset-requests/${id}/approve`)),

  listShifts: () => unwrap(api.get<ApiResponse<HrShift[]>>('/v1/hr/shifts')),

  createShift: (body: {
    name: string;
    shiftType?: string;
    startTime?: string;
    endTime?: string;
    breakMinutes?: number;
    workingDaysMask?: number;
    color?: string;
  }) =>
    unwrap(api.post<ApiResponse<HrShift>>('/v1/hr/shifts', body)),

  updateShift: (
    id: number,
    body: {
      name: string;
      shiftType?: string;
      startTime?: string;
      endTime?: string;
      breakMinutes?: number;
      workingDaysMask?: number;
      color?: string;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrShift>>(`/v1/hr/shifts/${id}`, body)),

  deactivateShift: (id: number) =>
    unwrap(api.post<ApiResponse<HrShift>>(`/v1/hr/shifts/${id}/deactivate`)),

  assignRoster: (body: {
    employeeId: number;
    shiftId: number;
    rosterDate: string;
    departmentId?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/roster', body)),

  replaceRosterDay: (body: {
    employeeId: number;
    rosterDate: string;
    shiftId?: number | null;
    departmentId?: number;
  }) => unwrap(api.put<ApiResponse<unknown>>('/v1/hr/roster/day', body)),

  listRoster: (from?: string, to?: string) =>
    unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/roster', { params: { from, to } })),

  uploadDocument: (
    file: File,
    employeeId: number,
    documentTypeId: number,
    details?: {
      documentName?: string;
      documentNumber?: string;
      issueDate?: string;
      expiryDate?: string;
    },
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('employeeId', String(employeeId));
    form.append('documentTypeId', String(documentTypeId));
    if (details?.documentName) form.append('documentName', details.documentName);
    if (details?.documentNumber) form.append('documentNumber', details.documentNumber);
    if (details?.issueDate) form.append('issueDate', details.issueDate);
    if (details?.expiryDate) form.append('expiryDate', details.expiryDate);
    return unwrap(
      api.post<ApiResponse<HrEmployeeDocumentRecord>>('/v1/hr/documents/upload', form, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }],
      }),
    );
  },

  essUploadDocument: (
    file: File,
    documentTypeId: number,
    details?: {
      documentName?: string;
      documentNumber?: string;
      issueDate?: string;
      expiryDate?: string;
    },
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('documentTypeId', String(documentTypeId));
    if (details?.documentName) form.append('documentName', details.documentName);
    if (details?.documentNumber) form.append('documentNumber', details.documentNumber);
    if (details?.issueDate) form.append('issueDate', details.issueDate);
    if (details?.expiryDate) form.append('expiryDate', details.expiryDate);
    return unwrap(
      api.post<ApiResponse<HrEmployeeDocumentRecord>>('/v1/hr/ess/documents/upload', form, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }],
      }),
    );
  },

  listWorkflows: () => unwrap(api.get<ApiResponse<HrWorkflowConfig[]>>('/v1/hr/workflows')),

  saveWorkflow: (body: {
    workflowName: string;
    workflowType: string;
    active?: boolean;
    stages: HrWorkflowConfig['stages'];
  }) => unwrap(api.put<ApiResponse<HrWorkflowConfig>>('/v1/hr/workflows', body)),

  getOrgStructure: () => unwrap(api.get<ApiResponse<HrOrgStructure>>('/v1/hr/org/structure')),

  createDepartment: (body: { name: string; code: string; parentDepartmentId?: number; businessUnitId?: number }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/departments', body)),

  createDesignation: (body: {
    name: string;
    code: string;
    parentDepartmentId?: number;
    gradeId?: number;
    grade?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/designations', body)),

  createGrade: (body: {
    name: string;
    code: string;
    levelOrder?: number;
    description?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/grades', body)),

  createBusinessUnit: (body: { name: string; code: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/org/business-units', body)),

  updateBusinessUnit: (id: number, body: { name: string; code: string; active?: boolean }) =>
    unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/business-units/${id}`, body)),

  deactivateBusinessUnit: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/business-units/${id}`)),

  updateDepartment: (
    id: number,
    body: { name: string; code: string; parentDepartmentId?: number; businessUnitId?: number; active?: boolean },
  ) => unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/departments/${id}`, body)),

  deactivateDepartment: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/departments/${id}`)),

  updateGrade: (
    id: number,
    body: { name: string; code: string; levelOrder?: number; description?: string; active?: boolean },
  ) => unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/grades/${id}`, body)),

  deactivateGrade: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/grades/${id}`)),

  updateDesignation: (
    id: number,
    body: {
      name: string;
      code: string;
      parentDepartmentId?: number;
      gradeId?: number;
      grade?: string;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<unknown>>(`/v1/hr/org/designations/${id}`, body)),

  deactivateDesignation: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/org/designations/${id}`)),

  assignManager: (employeeId: number, managerId: number, levelOrder?: number) =>
    unwrap(
      api.post<ApiResponse<unknown>>('/v1/hr/org/hierarchy', null, {
        params: { employeeId, managerId, levelOrder },
      }),
    ),

  removeManager: (employeeId: number) =>
    unwrap(
      api.delete<ApiResponse<null>>('/v1/hr/org/hierarchy', {
        params: { employeeId },
      }),
    ),

  hierarchyTree: (params?: { rootEmployeeId?: number; scope?: 'COMPANY' | 'MY_TEAM' }) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrHierarchyTreeResponse>>('/v1/hr/org/hierarchy-tree', { params })),

  reportingManagerPreview: (employeeId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrReportingManagerPreview | null>>(
      `/v1/hr/org/reporting-manager/${employeeId}`,
    )),

  mssTeam: (managerEmployeeId?: number) =>
    unwrap(
      api.get<ApiResponse<unknown[]>>('/v1/hr/mss/team', {
        params: managerEmployeeId ? { managerEmployeeId } : undefined,
      }),
    ),

  mssSummary: (managerEmployeeId?: number) =>
    unwrap(
      api.get<ApiResponse<HrManagerTeamSummary>>('/v1/hr/mss/summary', {
        params: managerEmployeeId ? { managerEmployeeId } : undefined,
      }),
    ),

  lifecycleHistory: (employeeId: number) =>
    unwrap(api.get<ApiResponse<HrLifecycleEvent[]>>(`/v1/hr/lifecycle/${employeeId}/history`)),

  recordLifecycleChange: (body: {
    employeeId: number;
    eventType: string;
    effectiveDate: string;
    metadataJson?: string;
  }) => unwrap(api.post<ApiResponse<HrLifecycleEvent>>('/v1/hr/lifecycle/change', body)),

  requestAttendanceAdjustment: (body: {
    employeeId: number;
    attendanceId?: number;
    requestedCheckIn?: string;
    requestedCheckOut?: string;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/attendance/adjustments', body)),

  approveAttendance: (id: number, approverId: number) =>
    unwrap(
      api.post<ApiResponse<HrAttendance>>(`/v1/hr/attendance/${id}/approve`, null, {
        params: { approverId },
      }),
    ),

  hireCandidate: (id: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${id}/hire`)),

  completeHire: (id: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${id}/complete-hire`)),

  scheduleInterview: (
    candidateId: number,
    body: { scheduledAt: string; interviewerEmployeeId?: number; interviewType?: string; roundName?: string; locationOrLink?: string; durationMinutes?: number },
  ) => unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${candidateId}/interviews`, body)),

  completeInterview: (interviewId: number, body: { score?: number; feedback?: string; recommendation?: string; strengths?: string; weaknesses?: string; decisionCode?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/interviews/${interviewId}/complete`, body)),

  createOffer: (
    candidateId: number,
    body: { salary: number; currency?: string; joiningDate?: string; validUntil?: string; content?: string; employmentType?: string },
  ) => unwrap(api.post<ApiResponse<{ id: number }>>(`/v1/hr/recruitment/candidates/${candidateId}/offers`, body)),

  acceptOffer: (offerId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/accept`)),

  declineOffer: (offerId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/decline`)),

  withdrawOffer: (offerId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/offers/${offerId}/withdraw`)),

  completeAllPreboarding: (candidateId: number) =>
    unwrap(api.post<ApiResponse<{ completedItems: number }>>(`/v1/hr/recruitment/candidates/${candidateId}/preboarding/complete-all`)),

  completeAllOnboarding: (candidateId: number) =>
    unwrap(api.post<ApiResponse<{ completedTasks: number }>>(`/v1/hr/recruitment/candidates/${candidateId}/onboarding/complete-all`)),

  approveRequisition: (id: number) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/requisitions/${id}/approve`)),

  recruitmentDashboard: () =>
    unwrap(api.get<ApiResponse<HrRecruitmentDashboard>>('/v1/hr/recruitment/dashboard')),

  recruitmentWorkQueue: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrRecruitmentWorkQueue>>('/v1/hr/recruitment/work-queue')),

  recruitmentAnalytics: () =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrRecruitmentAnalytics>>('/v1/hr/recruitment/analytics')),

  checkCandidateDuplicates: (params: {
    email?: string;
    phone?: string;
    nationalId?: string;
    linkedinUrl?: string;
  }) =>
    unwrap(
      api.get<ApiResponse<{ duplicateFound: boolean; matches: { candidateId: number; fullName: string; matchType: string }[] }>>(
        '/v1/hr/recruitment/candidates/duplicate-check',
        { params },
      ),
    ),

  runAutoScreening: (candidateId: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${candidateId}/screening/run`)),

  listCandidateAssessments: (candidateId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/hr').HrCandidateAssessment[]>>(`/v1/hr/recruitment/candidates/${candidateId}/assessments`)),

  scheduleAssessment: (candidateId: number, body: { assessmentType: string; assessmentName: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrCandidateAssessment>>(`/v1/hr/recruitment/candidates/${candidateId}/assessments`, body)),

  completeAssessment: (assessmentId: number, body: { score: number; feedback?: string; passed?: boolean }) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrCandidateAssessment>>(`/v1/hr/recruitment/assessments/${assessmentId}/complete`, body)),

  approveManpowerRequest: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrManpowerRequest>>(`/v1/hr/recruitment/workforce-planning/${id}/approve`)),

  approveCandidateSelection: (candidateId: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${candidateId}/approve-selection`)),

  approveCandidateOffer: (candidateId: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${candidateId}/approve-offer`)),

  confirmProbation: (reviewId: number, decision: 'CONFIRMED' | 'EXTENDED' | 'TERMINATED') =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/probation/${reviewId}/confirm`, null, { params: { decision } })),

  submitProbationReview: (reviewId: number, body: { managerRating?: number; hrRating?: number; managerComments?: string; hrComments?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/probation/${reviewId}/review`, body)),

  listManpowerRequests: () =>
    unwrap(api.get<ApiResponse<HrManpowerRequest[]>>('/v1/hr/recruitment/workforce-planning')),

  createManpowerRequest: (body: Partial<HrManpowerRequest>) =>
    unwrap(api.post<ApiResponse<HrManpowerRequest>>('/v1/hr/recruitment/workforce-planning', body)),

  submitManpowerRequest: (id: number) =>
    unwrap(api.post<ApiResponse<HrManpowerRequest>>(`/v1/hr/recruitment/workforce-planning/${id}/submit`)),

  listRequisitions: () =>
    unwrap(api.get<ApiResponse<HrJobRequisition[]>>('/v1/hr/recruitment/requisitions')),

  createRequisition: (body: Partial<HrJobRequisition>) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>('/v1/hr/recruitment/requisitions', body)),

  submitRequisition: (id: number) =>
    unwrap(api.post<ApiResponse<HrJobRequisition>>(`/v1/hr/recruitment/requisitions/${id}/submit`)),

  createPostingFromRequisition: (id: number, body: { title?: string; description?: string; statusCode?: string }) =>
    unwrap(api.post<ApiResponse<HrJobPosting>>(`/v1/hr/recruitment/requisitions/${id}/posting`, body)),

  candidate360: (id: number) =>
    unwrap(api.get<ApiResponse<Record<string, unknown>>>(`/v1/hr/recruitment/candidates/${id}/360`)),

  listOnboardingTasks: (candidateId: number) =>
    unwrap(api.get<ApiResponse<HrOnboardingTask[]>>(`/v1/hr/recruitment/candidates/${candidateId}/onboarding/tasks`)),

  completeOnboardingTask: (taskId: number, notes?: string) =>
    unwrap(
      api.post<ApiResponse<HrOnboardingTask>>(`/v1/hr/recruitment/onboarding/tasks/${taskId}/complete`, null, {
        params: notes ? { notes } : {},
      }),
    ),

  approveOnboarding: (candidateId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/candidates/${candidateId}/onboarding/approve`)),

  listPreboardingItems: (candidateId: number) =>
    unwrap(api.get<ApiResponse<HrPreboardingItem[]>>(`/v1/hr/recruitment/candidates/${candidateId}/preboarding`)),

  listPayrollTransactions: (runId: number) =>
    unwrap(api.get<ApiResponse<HrPayrollTransaction[]>>(`/v1/hr/payroll/runs/${runId}/transactions`)),

  updateFormula: (id: number, body: Partial<HrPayrollFormula>) =>
    unwrap(api.post<ApiResponse<HrPayrollFormula>>(`/v1/hr/payroll/formulas/${id}`, body)),

  previewFormula: (id: number, gross = 100000) =>
    unwrap(api.get<ApiResponse<Record<string, number>>>(`/v1/hr/payroll/formulas/${id}/preview`, { params: { gross } })),

  downloadDisbursementCsv: (runId: number) =>
    api.get(`/v1/hr/platform/payroll/runs/${runId}/disbursement`, { responseType: 'blob' }).then((r) => r.data as Blob),

  listLeaveTypes: () => unwrap(api.get<ApiResponse<HrLeaveType[]>>('/v1/hr/platform/leave/types')),
  createLeaveType: (body: {
    name: string;
    code: string;
    paid?: boolean;
    carryForward?: boolean;
    active?: boolean;
    color?: string;
    behavior?: Record<string, unknown>;
    categoryCode?: string;
  }) => unwrap(api.post<ApiResponse<HrLeaveType>>('/v1/hr/platform/leave/types', body)),
  updateLeaveType: (
    id: number,
    body: {
      name: string;
      code: string;
      paid?: boolean;
      carryForward?: boolean;
      active?: boolean;
      color?: string;
      behavior?: Record<string, unknown>;
      categoryCode?: string;
    },
  ) => unwrap(api.put<ApiResponse<HrLeaveType>>(`/v1/hr/platform/leave/types/${id}`, body)),
  deactivateLeaveType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/leave/types/${id}`)),
  listLeavePolicies: () => unwrap(api.get<ApiResponse<HrLeavePolicy[]>>('/v1/hr/platform/leave/policies')),
  saveLeavePolicy: (body: {
    leaveTypeId: number;
    policyName: string;
    accrualRuleJson?: string;
    eligibilityRuleJson?: string;
    annualEntitlementDays?: number;
    monthlyAccrualDays?: number;
    maxCarryForwardDays?: number;
    encashmentAllowed?: boolean;
    maxConsecutiveDays?: number;
    attachmentRequired?: boolean;
    approvalRequired?: boolean;
    probationBlocked?: boolean;
    allowBackdated?: boolean;
    maxBackdatedDays?: number;
    reserveOnSubmit?: boolean;
    policyActive?: boolean;
  }) => unwrap(api.put<ApiResponse<HrLeavePolicy>>('/v1/hr/platform/leave/policies', body)),

  listEmploymentTypesAdmin: () =>
    unwrap(api.get<ApiResponse<HrEmploymentType[]>>('/v1/hr/platform/employment-types')),
  createEmploymentType: (body: {
    name: string;
    code: string;
    category?: string;
    requiresEndDate?: boolean;
    payrollMode?: string;
    defaultHoursPerDay?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrEmploymentType>>('/v1/hr/platform/employment-types', body)),
  updateEmploymentType: (
    id: number,
    body: {
      name: string;
      code: string;
      category?: string;
      requiresEndDate?: boolean;
      payrollMode?: string;
      defaultHoursPerDay?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrEmploymentType>>(`/v1/hr/platform/employment-types/${id}`, body)),
  deactivateEmploymentType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/employment-types/${id}`)),

  listDocumentTypes: () => unwrap(api.get<ApiResponse<HrDocumentType[]>>('/v1/hr/platform/document-types')),
  createDocumentType: (body: { name: string; code: string; expiryTracked?: boolean; active?: boolean }) =>
    unwrap(api.post<ApiResponse<HrDocumentType>>('/v1/hr/platform/document-types', body)),
  updateDocumentType: (
    id: number,
    body: { name: string; code: string; expiryTracked?: boolean; active?: boolean },
  ) => unwrap(api.put<ApiResponse<HrDocumentType>>(`/v1/hr/platform/document-types/${id}`, body)),
  deactivateDocumentType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/document-types/${id}`)),

  listOvertimeTypes: () => unwrap(api.get<ApiResponse<HrOvertimeType[]>>('/v1/hr/platform/overtime-types')),
  createOvertimeType: (body: {
    name: string;
    code: string;
    description?: string;
    applicableDayClass?: string;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrOvertimeType>>('/v1/hr/platform/overtime-types', body)),
  updateOvertimeType: (
    id: number,
    body: {
      name: string;
      code: string;
      description?: string;
      applicableDayClass?: string;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrOvertimeType>>(`/v1/hr/platform/overtime-types/${id}`, body)),
  deactivateOvertimeType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/overtime-types/${id}`)),

  listShiftTypes: () => unwrap(api.get<ApiResponse<HrShiftType[]>>('/v1/hr/platform/shift-types')),
  createShiftType: (body: { name: string; code: string; description?: string; sortOrder?: number; active?: boolean }) =>
    unwrap(api.post<ApiResponse<HrShiftType>>('/v1/hr/platform/shift-types', body)),
  updateShiftType: (
    id: number,
    body: { name: string; code: string; description?: string; sortOrder?: number; active?: boolean },
  ) => unwrap(api.put<ApiResponse<HrShiftType>>(`/v1/hr/platform/shift-types/${id}`, body)),
  deactivateShiftType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/shift-types/${id}`)),

  listExpenseTypes: () => unwrap(api.get<ApiResponse<HrExpenseType[]>>('/v1/hr/platform/expense-types')),
  createExpenseType: (body: {
    name: string;
    code: string;
    description?: string;
    requiresReceipt?: boolean;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrExpenseType>>('/v1/hr/platform/expense-types', body)),
  updateExpenseType: (
    id: number,
    body: {
      name: string;
      code: string;
      description?: string;
      requiresReceipt?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrExpenseType>>(`/v1/hr/platform/expense-types/${id}`, body)),

  allowanceSummary: () => unwrap(api.get<ApiResponse<HrAllowanceSummary>>('/v1/hr/platform/allowance-summary')),
  listAllowanceTypes: () => unwrap(api.get<ApiResponse<HrAllowanceType[]>>('/v1/hr/platform/allowance-types')),
  createAllowanceType: (body: Partial<HrAllowanceType>) =>
    unwrap(api.post<ApiResponse<HrAllowanceType>>('/v1/hr/platform/allowance-types', body)),
  updateAllowanceType: (id: number, body: Partial<HrAllowanceType>) =>
    unwrap(api.put<ApiResponse<HrAllowanceType>>(`/v1/hr/platform/allowance-types/${id}`, body)),
  deactivateAllowanceType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/allowance-types/${id}`)),
  listAllowanceAssignments: (employeeId?: number) =>
    unwrap(
      api.get<ApiResponse<HrAllowanceAssignment[]>>('/v1/hr/platform/allowance-assignments', {
        params: employeeId ? { employeeId } : undefined,
      }),
    ),
  createAllowanceAssignment: (body: Partial<HrAllowanceAssignment>) =>
    unwrap(api.post<ApiResponse<HrAllowanceAssignment>>('/v1/hr/platform/allowance-assignments', body)),
  updateAllowanceAssignment: (id: number, body: Partial<HrAllowanceAssignment>) =>
    unwrap(api.put<ApiResponse<HrAllowanceAssignment>>(`/v1/hr/platform/allowance-assignments/${id}`, body)),
  deactivateAllowanceAssignment: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/allowance-assignments/${id}`)),
  allowanceAssignmentHistory: (id: number) =>
    unwrap(api.get<ApiResponse<HrAllowanceAssignmentHistory[]>>(`/v1/hr/platform/allowance-assignments/${id}/history`)),
  myAllowances: () => unwrap(api.get<ApiResponse<HrAllowanceAssignment[]>>('/v1/hr/ess/allowances')),

  listRecruitmentPipelineStages: () =>
    unwrap(api.get<ApiResponse<HrRecruitmentPipelineStage[]>>('/v1/hr/platform/recruitment/pipeline-stages')),
  createRecruitmentPipelineStage: (body: {
    name: string;
    code: string;
    color?: string;
    sortOrder?: number;
    active?: boolean;
  }) =>
    unwrap(
      api.post<ApiResponse<HrRecruitmentPipelineStage>>('/v1/hr/platform/recruitment/pipeline-stages', body),
    ),
  updateRecruitmentPipelineStage: (
    id: number,
    body: { name: string; code: string; color?: string; sortOrder?: number; active?: boolean },
  ) =>
    unwrap(
      api.put<ApiResponse<HrRecruitmentPipelineStage>>(`/v1/hr/platform/recruitment/pipeline-stages/${id}`, body),
    ),
  deactivateRecruitmentPipelineStage: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/recruitment/pipeline-stages/${id}`)),

  deactivateExpenseType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/expense-types/${id}`)),

  listAssetTypes: () => unwrap(api.get<ApiResponse<HrAssetType[]>>('/v1/hr/platform/asset-types')),
  createAssetType: (body: {
    code: string;
    name: string;
    description?: string;
    requiresReturn?: boolean;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrAssetType>>('/v1/hr/platform/asset-types', body)),
  updateAssetType: (
    id: number,
    body: {
      code: string;
      name: string;
      description?: string;
      requiresReturn?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrAssetType>>(`/v1/hr/platform/asset-types/${id}`, body)),
  deactivateAssetType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/asset-types/${id}`)),

  requestOvertime: (body: { employeeId: number; overtimeDate: string; minutes: number; overtimeType: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/overtime', body)),
  listOvertime: (params?: { startDate?: string; endDate?: string }) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrOvertimeRequest[]>>('/v1/hr/overtime', {
        params: params?.startDate && params?.endDate
          ? { startDate: params.startDate, endDate: params.endDate }
          : undefined,
      }),
    ),
  approveOvertime: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrOvertimeRequest>>(`/v1/hr/overtime/${id}/approve`)),
  cancelOvertime: (id: number) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrOvertimeRequest>>(`/v1/hr/overtime/${id}/cancel`)),

  startOnboarding: (employeeId: number, managerId?: number) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>(`/v1/hr/onboarding/${employeeId}/start`, null, { params: { managerId } })),
  initiateExit: (body: { employeeId: number; exitType?: string; lastWorkingDay?: string; noticePeriodDays?: number }) =>
    unwrap(api.post<ApiResponse<Record<string, unknown>>>('/v1/hr/exit/initiate', body)),
  finalizeExit: (employeeId: number) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/exit/${employeeId}/finalize`)),

  listEmployeeDocuments: (employeeId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`/v1/hr/documents/employee/${employeeId}`)),

  listCompensationStructures: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/compensation/structures')),
  createCompensationStructure: (name: string, currencyCode?: string, countryCode?: string) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/platform/compensation/structures', null, { params: { name, currencyCode, countryCode } })),
  assignCompensation: (body: { employeeId: number; structureId: number; effectiveFrom: string; grossAmount: number; effectiveTo?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/compensation/assignments', body)),
  listCompensationAssignments: (employeeId: number) =>
    unwrap(api.get<ApiResponse<unknown[]>>(`/v1/hr/compensation/assignments/${employeeId}`)),

  assignTraining: (body: { employeeId: number; courseId: number; dueDate?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/training/assignments', body)),

  listTravel: () => unwrap(api.get<ApiResponse<HrTravelRequest[]>>('/v1/hr/platform/travel')),
  createTravel: (params: Record<string, string | number | undefined>) =>
    unwrap(api.post<ApiResponse<HrTravelRequest>>('/v1/hr/platform/travel', null, { params })),
  updateTravel: (id: number, body: Record<string, string | number | undefined | null>) =>
    unwrap(api.put<ApiResponse<HrTravelRequest>>(`/v1/hr/platform/travel/${id}`, body)),
  settleTravel: (id: number, amount: number) =>
    unwrap(
      api.post<ApiResponse<HrTravelRequest>>(`/v1/hr/platform/travel/${id}/settle`, null, {
        params: { amount },
      }),
    ),
  approveTravel: (id: number) => unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/platform/travel/${id}/approve`)),

  listSuccessionPlans: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/succession')),
  createSuccessionPlan: (params: Record<string, string | number | undefined>) =>
    unwrap(api.post<ApiResponse<unknown>>('/v1/hr/platform/succession', null, { params })),

  initiateBgv: (candidateId: number, type?: string, vendor?: string) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/hr/platform/recruitment/candidates/${candidateId}/bgv`, null, { params: { type, vendor } })),

  completeBgv: (bgvId: number, status?: string, summary?: string) =>
    unwrap(
      api.post<ApiResponse<unknown>>(`/v1/hr/recruitment/bgv/${bgvId}/complete`, null, {
        params: { status, summary },
      }),
    ),

  listFieldPermissions: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/admin/field-permissions')),
  listCountryPacks: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/admin/country-packs')),
  listShiftRotations: () => unwrap(api.get<ApiResponse<unknown[]>>('/v1/hr/platform/admin/shift-rotations')),

  publicCareersJobs: (organizationId: number) =>
    unwrap(api.get<ApiResponse<HrJobPosting[]>>(`/v1/public/careers/${organizationId}/jobs`)),
  publicCareersApply: (organizationId: number, body: { jobPostingId: number; fullName: string; email?: string; phone?: string }) =>
    unwrap(api.post<ApiResponse<unknown>>(`/v1/public/careers/${organizationId}/apply`, body)),

  listEmploymentTypes: () =>
    unwrap(api.get<ApiResponse<HrEmploymentType[]>>('/v1/hr/workforce/employment-types')),

  employmentHistory: (employeeId: number) =>
    unwrap(api.get<ApiResponse<HrEmploymentHistory[]>>(`/v1/hr/workforce/employees/${employeeId}/employment-history`)),

  changeEmploymentType: (body: {
    employeeId: number;
    employmentTypeCode: string;
    effectiveDate: string;
    endDate?: string;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrEmploymentHistory>>('/v1/hr/workforce/employment-type/change', body)),

  expectedHours: (employeeId: number, periodStart: string, periodEnd: string) =>
    unwrap(
      api.get<ApiResponse<HrExpectedHours>>(`/v1/hr/workforce/employees/${employeeId}/expected-hours`, {
        params: { periodStart, periodEnd },
      }),
    ),

  previewLeaveDays: (
    employeeId: number,
    selectedDates: string[],
    opts?: { durationMode?: string; halfDaySession?: string; leaveHours?: number },
  ) => {
    const sorted = [...selectedDates].sort();
    return unwrap(
      api.get<ApiResponse<HrLeaveDaysPreview>>(`/v1/hr/workforce/employees/${employeeId}/leave-days-preview`, {
        params: {
          selectedDates: sorted,
          startDate: sorted[0],
          endDate: sorted[sorted.length - 1],
          ...opts,
        },
      }),
    );
  },

  listCompanyHolidays: (start: string, end: string) =>
    unwrap(
      api.get<ApiResponse<HrCompanyHoliday[]>>('/v1/hr/workforce/holidays', { params: { start, end } }),
    ),

  createHoliday: (body: {
    holidayDate: string;
    holidayName: string;
    holidayType?: string;
    premiumRate?: number | null;
    treatmentCode?: string | null;
  }) => unwrap(api.post<ApiResponse<HrCompanyHoliday>>('/v1/hr/workforce/holidays', body)),

  updateHoliday: (
    id: number,
    body: {
      holidayDate: string;
      holidayName: string;
      holidayType?: string;
      premiumRate?: number | null;
      treatmentCode?: string | null;
    },
  ) => unwrap(api.put<ApiResponse<HrCompanyHoliday>>(`/v1/hr/workforce/holidays/${id}`, body)),

  deleteHoliday: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/holidays/${id}`)),

  listHolidayTypes: (includeInactive = true) =>
    unwrap(
      api.get<ApiResponse<HrHolidayType[]>>('/v1/hr/workforce/holiday-types', {
        params: { includeInactive },
      }),
    ),

  createHolidayType: (body: {
    code: string;
    name: string;
    description?: string;
    nonWorkingDay?: boolean;
    sortOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrHolidayType>>('/v1/hr/workforce/holiday-types', body)),

  updateHolidayType: (
    id: number,
    body: {
      code: string;
      name: string;
      description?: string;
      nonWorkingDay?: boolean;
      sortOrder?: number;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<HrHolidayType>>(`/v1/hr/workforce/holiday-types/${id}`, body)),

  deactivateHolidayType: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/holiday-types/${id}`)),

  listWeekPatterns: (includeInactive = true) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrWeekPattern[]>>('/v1/hr/workforce/week-patterns', {
        params: { includeInactive },
      }),
    ),

  createWeekPattern: (body: import('@/types/hr').HrWeekPatternForm) =>
    unwrap(api.post<ApiResponse<import('@/types/hr').HrWeekPattern>>('/v1/hr/workforce/week-patterns', body)),

  updateWeekPattern: (id: number, body: import('@/types/hr').HrWeekPatternForm) =>
    unwrap(api.put<ApiResponse<import('@/types/hr').HrWeekPattern>>(`/v1/hr/workforce/week-patterns/${id}`, body)),

  deactivateWeekPattern: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/week-patterns/${id}`)),

  resolvedCalendar: (employeeId: number, start: string, end: string) =>
    unwrap(
      api.get<ApiResponse<HrResolvedCalendar>>(`/v1/hr/workforce/employees/${employeeId}/resolved-calendar`, {
        params: { start, end },
      }),
    ),

  resolvedCalendarBatch: (employeeIds: number[], start: string, end: string) =>
    unwrap(
      api.get<ApiResponse<HrResolvedCalendar[]>>('/v1/hr/workforce/resolved-calendar', {
        params: { employeeIds, start, end },
      }),
    ),

  calendarLeaves: (params: SearchParams) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrLeaveRequest[]>>('/v1/hr/workforce/calendar/leaves', {
        params,
      }),
    ),

  calendarAttendance: (params: SearchParams) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrAttendance[]>>('/v1/hr/workforce/calendar/attendance', {
        params,
      }),
    ),

  assignEmployeeShift: (body: {
    employeeId: number;
    shiftId: number;
    selectedDates?: string[];
    effectiveFrom?: string;
    effectiveTo?: string;
  }) => unwrap(api.post<ApiResponse<import('@/types/hr').HrScheduleChangeRequest>>('/v1/hr/shifts/assignments', body)),

  cancelShiftChange: (id: number) =>
    unwrap(
      api.post<ApiResponse<import('@/types/hr').HrScheduleChangeRequest>>(
        `/v1/hr/shifts/change-requests/${id}/cancel`,
      ),
    ),

  listShiftChangeRequests: (params?: {
    employeeIds?: number[];
    start?: string;
    end?: string;
    employeeId?: number;
    manageable?: boolean;
  }) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrScheduleChangeRequest[]>>('/v1/hr/shifts/change-requests', {
        params: params
          ? {
              employeeIds: params.employeeIds,
              start: params.start,
              end: params.end,
              employeeId: params.employeeId,
              manageable: params.manageable,
            }
          : undefined,
      }),
    ),

  listShiftAssignments: (employeeId?: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/hr').HrEmployeeShiftAssignment[]>>('/v1/hr/shifts/assignments', {
        params: employeeId != null ? { employeeId } : undefined,
      }),
    ),

  assignDayShift: (body: {
    employeeId: number;
    assignmentDate: string;
    shiftId: number;
    sequenceOrder?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/hr/shifts/day-assignments', body)),

  assignDayShiftsBulk: (body: {
    employeeId: number;
    assignmentDate: string;
    shiftIds: number[];
  }) => unwrap(api.post<ApiResponse<unknown[]>>('/v1/hr/shifts/day-assignments/bulk', body)),

  removeDayShift: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/shifts/day-assignments/${id}`)),

  listDayShifts: (employeeId: number, from?: string, to?: string) =>
    unwrap(
      api.get<ApiResponse<unknown[]>>('/v1/hr/shifts/day-assignments', {
        params: { employeeId, from, to },
      }),
    ),

  monthlyAttendanceSummary: (employeeId: number, year: number, month: number) =>
    unwrap(
      api.get<ApiResponse<HrMonthlyAttendanceSummary>>(
        `/v1/hr/workforce/employees/${employeeId}/attendance-summary`,
        { params: { year, month } },
      ),
    ),

  runMonthEnd: (year: number, month: number) =>
    unwrap(api.post<ApiResponse<unknown[]>>('/v1/hr/workforce/month-end/run', null, { params: { year, month } })),

  employeeWorkforceDashboard: (employeeId: number) =>
    unwrap(api.get<ApiResponse<HrEmployeeWorkforceDashboard>>(`/v1/hr/workforce/dashboard/employee/${employeeId}`)),

  managerWorkforceDashboard: (managerId: number) =>
    unwrap(api.get<ApiResponse<HrManagerWorkforceDashboard>>(`/v1/hr/workforce/dashboard/manager/${managerId}`)),

  hrWorkforceDashboard: () =>
    unwrap(api.get<ApiResponse<HrHrWorkforceDashboard>>('/v1/hr/workforce/dashboard/hr')),

  listCalendarOverrides: (employeeId: number, start?: string, end?: string) =>
    unwrap(
      api.get<ApiResponse<HrWorkCalendarOverride[]>>(
        `/v1/hr/workforce/employees/${employeeId}/calendar-overrides`,
        { params: { start, end } },
      ),
    ),

  upsertCalendarOverride: (body: {
    employeeId: number;
    calendarDate: string;
    dayType: string;
    startTime?: string;
    endTime?: string;
    isHalfDay?: boolean;
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrWorkCalendarOverride>>('/v1/hr/workforce/calendar-overrides', body)),

  deleteCalendarOverride: (employeeId: number, date: string) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/workforce/employees/${employeeId}/calendar-overrides/${date}`)),

  listWeeklyOvertime: (employeeId: number, periodStart?: string, periodEnd?: string) =>
    unwrap(
      api.get<ApiResponse<HrWeeklyOvertimeSummary[]>>(
        `/v1/hr/workforce/employees/${employeeId}/weekly-overtime`,
        { params: { periodStart, periodEnd } },
      ),
    ),

  aggregateWeeklyOvertime: (employeeId: number, weekStart?: string) =>
    unwrap(
      api.post<ApiResponse<HrWeeklyOvertimeSummary>>('/v1/hr/workforce/weekly-overtime/aggregate', null, {
        params: { employeeId, weekStart },
      }),
    ),

  aggregateCompanyWeeklyOvertime: (weekStart?: string) =>
    unwrap(
      api.post<ApiResponse<{ employeesProcessed: number }>>(
        '/v1/hr/workforce/weekly-overtime/aggregate-company',
        null,
        { params: { weekStart } },
      ),
    ),

  confirmDueProbations: () =>
    unwrap(api.post<ApiResponse<{ confirmed: number }>>('/v1/hr/workforce/probation/confirm-due')),

  downloadHrImportTemplate: (type: 'attendance' | 'shifts' | 'overtime', lang?: string) =>
    api
      .get(`/v1/hr/import/${type}/template`, {
        params: lang ? { lang } : undefined,
        responseType: 'blob',
      })
      .then((r) => r.data as Blob),

  validateHrImport: (type: 'attendance' | 'shifts' | 'overtime', file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<import('@/modules/hr/components/hrCsvImportTypes').HrCsvImportResponse>>(
        `/v1/hr/import/${type}/validate`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    );
  },

  importHrCsv: (type: 'attendance' | 'shifts' | 'overtime', file: File, skipErrors = false) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<import('@/modules/hr/components/hrCsvImportTypes').HrCsvImportResponse>>(
        `/v1/hr/import/${type}/import`,
        form,
        {
          params: { skipErrors },
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      ),
    );
  },

  listTimesheetDays: (employeeId: number, from: string, to: string) =>
    unwrap(
      api.get<ApiResponse<HrTimesheetDay[]>>('/v1/hr/timesheet', {
        params: { employeeId, from, to },
      }),
    ),

  timesheetSummary: (employeeId: number, year: number, month: number) =>
    unwrap(
      api.get<ApiResponse<HrTimesheetSummary>>('/v1/hr/timesheet/summary', {
        params: { employeeId, year, month },
      }),
    ),

  compRestOverview: (employeeId: number, year: number) =>
    unwrap(
      api.get<ApiResponse<HrCompRestOverview>>('/v1/hr/timesheet/comp-rest', {
        params: { employeeId, year },
      }),
    ),

  essTimesheet: (year?: number, month?: number) =>
    unwrap(
      api.get<ApiResponse<HrEssTimesheet>>('/v1/hr/ess/timesheet', {
        params: {
          ...(year != null ? { year } : {}),
          ...(month != null ? { month } : {}),
        },
      }),
    ),

  listAbsenceCategories: (includeInactive = false) =>
    unwrap(
      api.get<ApiResponse<HrAbsenceCategory[]>>('/v1/hr/platform/absence-categories', {
        params: { includeInactive },
      }),
    ),

  createAbsenceCategory: (body: {
    code: string;
    name: string;
    displayOrder?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<HrAbsenceCategory>>('/v1/hr/platform/absence-categories', body)),

  updateAbsenceCategory: (
    id: number,
    body: { code: string; name: string; displayOrder?: number; active?: boolean },
  ) => unwrap(api.put<ApiResponse<HrAbsenceCategory>>(`/v1/hr/platform/absence-categories/${id}`, body)),

  deactivateAbsenceCategory: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/hr/platform/absence-categories/${id}`)),

  previewRosterCopy: (body: HrRosterCopyRequest) =>
    unwrap(api.post<ApiResponse<HrRosterCopyPreview>>('/v1/hr/roster/copy/preview', body)),

  copyRoster: (body: HrRosterCopyRequest) =>
    unwrap(api.post<ApiResponse<HrRosterCopyResult>>('/v1/hr/roster/copy', body)),

  publishRoster: (weekStart: string, copyRequest?: HrRosterCopyRequest) =>
    unwrap(
      api.post<ApiResponse<unknown>>('/v1/hr/roster/publish', copyRequest ?? null, {
        params: { weekStart },
      }),
    ),

  listEssShiftSwaps: () =>
    unwrap(api.get<ApiResponse<HrShiftSwapRequest[]>>('/v1/hr/ess/shift-swaps')),

  listEssIncomingShiftSwaps: () =>
    unwrap(api.get<ApiResponse<HrShiftSwapRequest[]>>('/v1/hr/ess/shift-swaps/incoming')),

  createEssShiftSwap: (body: {
    targetEmployeeId: number;
    requesterDate: string;
    targetDate?: string;
    requesterShiftId?: number;
    targetShiftId?: number;
    swapType?: 'SWAP' | 'COVER';
    reason?: string;
  }) => unwrap(api.post<ApiResponse<HrShiftSwapRequest>>('/v1/hr/ess/shift-swaps', body)),

  respondEssShiftSwap: (id: number, accept: boolean) =>
    unwrap(
      api.post<ApiResponse<HrShiftSwapRequest>>(`/v1/hr/ess/shift-swaps/${id}/respond`, null, {
        params: { accept },
      }),
    ),

  cancelEssShiftSwap: (id: number) =>
    unwrap(api.post<ApiResponse<HrShiftSwapRequest>>(`/v1/hr/ess/shift-swaps/${id}/cancel`)),

  payrollHandoffPreview: (year: number, month: number, employeeId?: number) =>
    unwrap(
      api.get<ApiResponse<HrPayrollHandoffResponse>>('/v1/hr/reports/payroll-handoff/preview', {
        params: { year, month, ...(employeeId ? { employeeId } : {}) },
      }),
    ),

  downloadPayrollHandoff: async (
    year: number,
    month: number,
    format: 'csv' | 'CSV' | 'EXCEL' | 'xlsx' | 'XLSX' = 'csv',
    employeeId?: number,
  ) => {
    const res = await api.get('/v1/hr/reports/payroll-handoff', {
      params: { year, month, format, ...(employeeId ? { employeeId } : {}) },
      responseType: 'blob',
    });
    return res.data as Blob;
  },
};
